<template>
  <li
    :aria-current="currentStep() === step ? 'step' : undefined"
    class="tw:relative tw:flex tw:flex-1 tw:leading-none tw:transition-opacity tw:duration-150"
    :class="stepClasses"
    data-testid="progress-step"
    :style="stepStyle"
    @click="handleClick"
  >
    <component
      :is="isInteractive ? 'button' : 'div'"
      class="tw:m-0 tw:flex tw:w-full tw:items-inherit tw:border-0 tw:bg-transparent tw:p-0 tw:text-inherit tw:[font:inherit] tw:text-left tw:outline-none"
      :class="[
        stacked ? 'tw:justify-between' : 'tw:justify-center',
        isInteractive ? 'tw:cursor-pointer' : '',
      ]"
      data-testid="progress-step-trigger"
      :type="isInteractive ? 'button' : undefined"
    >
      <div
        v-if="step > 1 && !stacked"
        class="c-progress-step__arrow"
        data-testid="progress-step-arrow"
      >
        <span />
      </div>
      <div class="c-progress-step__content-wrapper tw:flex">
        <div
          class="c-progress-step__icon"
          data-testid="progress-step-icon"
        >
          <QIcon
            v-if="completed"
            name="ion-checkmark"
          />
          <span
            v-else
            aria-hidden="true"
          >
            {{ step }}
          </span>
        </div>
        <div class="tw:ml-2">
          <div
            class="tw:font-bold"
            data-testid="progress-step-label"
          >
            {{ label }}
          </div>
          <div
            class="c-progress-step__description tw:relative tw:mt-1.5"
            data-testid="progress-step-description"
          >
            <template v-if="showDescription">
              <span v-if="description">{{ description }}</span>
              <span v-if="completed">{{ getLocaleText("global.done") }}</span>
            </template>
          </div>
        </div>
      </div>
      <div
        v-if="showCollapsedDescription"
        data-testid="progress-step-collapsed-description"
      >
        <span>{{ collapsedDescription }}</span>
      </div>
    </component>
  </li>
</template>

<script setup lang="ts">
import type { CSSProperties } from "vue";
import { computed, inject } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  ProgressStepCurrentSymbol,
  ProgressStepSelectSymbol,
} from "shared/components/base/symbols";
import type { Nullable } from "shared/types/utility";

interface ProgressStepProps {
  collapsedDescription?: Nullable<string>;
  completed?: boolean;
  description?: Nullable<string>;
  enabled?: boolean;
  label: string;
  stacked?: boolean;
  step: number;
  stepStyle?: CSSProperties;
}

interface ProgressStepEmits {
  click: [event: MouseEvent];
}

const props = withDefaults(defineProps<ProgressStepProps>(), {
  description: null,
  collapsedDescription: null,
  stepStyle: () => ({}),
});

const emit = defineEmits<ProgressStepEmits>();

const currentStep = inject(ProgressStepCurrentSymbol, () => -1);
const selectStep = inject(ProgressStepSelectSymbol, () => {});

const stepClasses = computed(() => {
  const classes = ["c-progress-step"];
  if (currentStep() === props.step) classes.push("c-progress-step__current");
  if (props.enabled) classes.push("c-progress-step__enabled");
  if (props.completed) classes.push("c-progress-step__completed");
  if (props.stacked) classes.push("c-progress-step__stacked");

  return classes;
});

const showDescription = computed(() => {
  if (props.stacked && props.completed) return false;

  return true;
});

const showCollapsedDescription = computed(() => {
  if (props.stacked && currentStep() !== props.step) return true;

  return false;
});

const isInteractive = computed(() => props.completed || props.enabled);

function handleClick(event: MouseEvent) {
  if (!isInteractive.value) return;

  selectStep({ step: props.step });
  emit("click", event);
}
</script>

<style lang="scss" scoped>
.c-progress-step {
  --c-progress-step-color: var(--s-color-primary);
  --c-progress-step-icon-bg: transparent;
  --c-progress-step-icon-text: var(--c-progress-step-color);

  justify-content: center;
  opacity: 0.8;
  padding: 16px;
  border-bottom: 2px solid var(--c-progress-step-color);

  &__stacked {
    border-bottom: none;
    justify-content: space-between;
    padding: 12px 16px 10px;
  }

  &:has(:focus-visible) {
    box-shadow:
      inset 0 1px 0 0 var(--c-progress-step-color),
      inset 1px 0 0 0 var(--c-progress-step-color),
      inset -1px 0 0 0 var(--c-progress-step-color);
  }

  &__stacked:has(:focus-visible) {
    box-shadow:
      inset 0 1px 0 0 var(--c-progress-step-color),
      inset 1px 0 0 0 var(--c-progress-step-color),
      inset -1px 0 0 0 var(--c-progress-step-color),
      inset 0 -1px 0 0 var(--c-progress-step-color);
  }

  &__arrow {
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;

    span {
      border-right: 2px solid var(--c-progress-step-color);
      width: 12px;
      height: 12px;
      display: block;
      position: absolute;
      border-top: 2px solid var(--c-progress-step-color);
      transform: rotate(45deg);
      border-top-right-radius: 2px;
      left: -4px;
    }
  }

  &__icon {
    width: 24px;
    height: 24px;
    border-radius: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 1;
    border: 1px solid var(--c-progress-step-color);
    margin-top: -4px;
    background-color: var(--c-progress-step-icon-bg);
    color: var(--c-progress-step-icon-text);
    transition:
      color 150ms,
      background-color 150ms;
  }

  &__current {
    --c-progress-step-icon-bg: var(--c-progress-step-color);
    --c-progress-step-icon-text: white;

    opacity: 1;
  }

  &__description {
    span {
      white-space: nowrap;
    }
  }

  &__enabled {
    cursor: pointer;
    opacity: 1;
  }

  &__completed {
    opacity: 1;
    cursor: pointer;

    .c-progress-step__description {
      span:first-child {
        visibility: hidden;
      }

      span:last-child {
        position: absolute;
        z-index: 1;
        left: 0;
        top: 0;
      }
    }

    &:hover:not(.c-progress-step__current) {
      --c-progress-step-icon-bg: #{$ds-denim-3};
    }
  }
}
</style>
